import React from "react";
import { debounce } from "shared/utils";

/**
 * A custom React hook that creates a debounced version of a callback function.
 * @param {Function} callback - The function to be debounced.
 * @param {number} [delay=1000] - The delay (in milliseconds) before invoking the debounced callback.
 * @param {Array} [deps=[]] - An array of dependencies to include in the useEffect hook.
 * @returns {Function|Object} A debounced version of the provided callback function. If `callback` is called, it returns an object containing a `cancel` function to cancel the debounce.
 */
export function useDebounce(callback, delay = 1000, deps = []) {
  const debouncedCallback = React.useCallback(debounce(callback, delay), [
    delay,
    ...deps,
  ]);

  /**
   * Effect hook that cancels the debounce when component unmounts or dependencies change.
   */
  React.useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [delay, ...deps]);

  return debouncedCallback;
}
